/**
 * documentTokens.js — Shared design tokens for all SMA document templates
 * (receipts, invoices, curriculum sheets, certificates, passes).
 *
 * Design philosophy: clean, minimalist corporate look.
 *  - One accent color (muted terracotta) used sparingly for 1-2 highlights
 *  - Hairline dividers instead of shaded/striped backgrounds
 *  - Quiet labels: small, gray, sentence case
 *  - Header: logo left, contact right, hairline rule, title below
 *  - Light footer with hairline top
 *  - Outlined pill status badges in accent color
 *
 * Every document component imports from here so they all inherit the same
 * look automatically — no per-component color definitions.
 */

// ── Color palette ───────────────────────────────────────────────────────────

/** Single accent color — muted terracotta. Reserved for amount due, status
 *  badge outlines, and one or two meaningful highlights per document. */
export const ACCENT = '#a35d2c'

/** Primary text color — near-black, used for values and titles. */
export const INK = '#1c1c1c'

/** Secondary text color — muted gray for labels, contact info, footers. */
export const MUTE = '#6f6f6f'

/** Hairline divider color — warm light gray. */
export const RULE = '#e2e0db'

/** Page background — warm off-white. */
export const PAPER = '#fafaf8'

/** White — for card/document backgrounds. */
export const WHITE = '#ffffff'

/** Soft accent tint — light terracotta wash for callout/enroll blocks.
 *  Paired with a 2px left border in ACCENT. */
export const ACCENT_SOFT = '#f6efe8'

/** Status colors — used sparingly, only for semantic meaning. */
export const STATUS = {
  paid: ACCENT,
  partial: ACCENT,
  pending: ACCENT,
  overdue: '#c0392b',
  confirmed: ACCENT,
  pendingVerify: ACCENT,
  unpaid: ACCENT,
}

// ── Typography ──────────────────────────────────────────────────────────────

export const FONT_STACK =
  'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'

export const DISPLAY_FONT = '"Outfit", system-ui, sans-serif'

/** Serif font for document titles (course names, "Invoice", etc.) —
 *  matches the Georgia serif used in the reference mockup. */
export const SERIF_FONT = 'Georgia, "Times New Roman", serif'

// ── Spacing ─────────────────────────────────────────────────────────────────

/** Horizontal padding inside the document body. */
export const PAD_X = '40px'
export const PAD_X_MOBILE = '16px'

/** Vertical padding for label/value rows — consistent across all documents. */
export const ROW_PAD_Y = '16px'
export const ROW_PAD_Y_MOBILE = '12px'

/** Section spacing between major blocks. */
export const SECTION_GAP = '32px'
export const SECTION_GAP_MOBILE = '20px'

// ── Border styles ───────────────────────────────────────────────────────────

/** Standard hairline divider — 1px solid in RULE color. */
export const HAIRLINE = `1px solid ${RULE}`

// ── Helper functions ────────────────────────────────────────────────────────

/**
 * Build a label/value row style object consistent across all documents.
 * Uses hairline bottom divider, quiet label, and consistent padding.
 *
 * @param {boolean} isMobile
 * @param {Object} [overrides] - Override specific style properties
 * @returns {Object} React style object for the row container
 */
export function rowStyle(isMobile, overrides = {}) {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: isMobile ? '8px' : '16px',
    padding: `${isMobile ? ROW_PAD_Y_MOBILE : ROW_PAD_Y} 0`,
    borderBottom: HAIRLINE,
    ...overrides,
  }
}

/**
 * Build a quiet label style — small, gray, sentence case.
 * Not bold, not uppercase — lets the value carry attention.
 *
 * @param {boolean} isMobile
 * @returns {Object} React style object for the label span
 */
export function labelStyle(isMobile) {
  return {
    fontSize: isMobile ? '10px' : '12px',
    color: MUTE,
    fontWeight: '400',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  }
}

/**
 * Build a value style — dark, medium weight, right-aligned.
 *
 * @param {boolean} isMobile
 * @param {Object} [overrides] - e.g. { color: ACCENT, fontWeight: '700' }
 * @returns {Object} React style object for the value span
 */
export function valueStyle(isMobile, overrides = {}) {
  return {
    fontSize: isMobile ? '11px' : '13px',
    color: INK,
    fontWeight: '500',
    textAlign: 'right',
    wordBreak: 'break-word',
    lineHeight: 1.3,
    ...overrides,
  }
}

/**
 * Build an outlined pill badge style — accent border, transparent background.
 * Used for status badges (Fully Paid, Partial Payment, etc.)
 *
 * @param {boolean} isMobile
 * @param {string} [color] - Override border/text color (defaults to ACCENT)
 * @returns {Object} React style object for the badge span
 */
export function pillBadgeStyle(isMobile, color = ACCENT) {
  return {
    display: 'inline-block',
    padding: isMobile ? '4px 12px' : '5px 14px',
    fontSize: isMobile ? '9px' : '10.5px',
    fontWeight: '600',
    letterSpacing: '0.03em',
    color,
    border: `1px solid ${color}`,
    background: 'transparent',
    whiteSpace: 'nowrap',
  }
}

/**
 * Build the document header style — logo left, contact right, hairline below.
 * No dark background, no colored bars.
 *
 * @param {boolean} isMobile
 * @returns {Object} React style object for the header container
 */
export function headerStyle(isMobile) {
  return {
    padding: isMobile
      ? `20px ${PAD_X_MOBILE} 16px`
      : `28px ${PAD_X} 20px`,
    background: WHITE,
  }
}

/**
 * Build the document footer style — hairline top, muted text.
 * No dark background bars.
 *
 * @param {boolean} isMobile
 * @returns {Object} React style object for the footer container
 */
export function footerStyle(isMobile) {
  return {
    borderTop: HAIRLINE,
    padding: isMobile
      ? `14px ${PAD_X_MOBILE} 20px`
      : `16px ${PAD_X} 24px`,
    background: WHITE,
  }
}

/**
 * Build the document body container style.
 *
 * @param {boolean} isMobile
 * @returns {Object} React style object for the body container
 */
export function bodyStyle(isMobile) {
  return {
    padding: isMobile
      ? `16px ${PAD_X_MOBILE} 24px`
      : `24px ${PAD_X} 32px`,
    background: WHITE,
    boxSizing: 'border-box',
  }
}

/**
 * Build the outer document wrapper style.
 *
 * @param {boolean} isMobile
 * @param {string} [maxWidth] - Override max width (default: '794px' for A4 portrait)
 * @returns {Object} React style object for the document root
 */
export function documentStyle(isMobile, maxWidth = '794px') {
  return {
    fontFamily: FONT_STACK,
    width: '100%',
    maxWidth: isMobile ? '100%' : maxWidth,
    background: WHITE,
    color: INK,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  }
}

/**
 * Format UGX currency consistently across all documents.
 */
export function formatUGX(amount) {
  const n = parseFloat(amount)
  if (isNaN(n)) return '—'
  return `UGX ${n.toLocaleString('en-US')}`
}

/**
 * Format dates consistently across all documents.
 */
export function formatDate(dateStr, includeTime = false) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return '—'
  const datePart = d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  if (!includeTime) return datePart
  return `${datePart} · ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
}
